import { useState, useEffect } from "react";
import { DollarSign, Clock, CheckCircle2, RotateCcw, CreditCard, Shield, Ticket } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { formatRupiah } from "./ui";

const STATUS_CONFIG = {
  PENDING: { label: "Menunggu Pembayaran", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", icon: Clock },
  HELD: { label: "Dana Ditahan (Escrow)", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", icon: CreditCard },
  PARTIALLY_RELEASED: { label: "30% Diterima, 70% Dilindungi", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", icon: Shield },
  RELEASED: { label: "Pembayaran Selesai", color: "text-success-600", bg: "bg-success-50", border: "border-success-200", icon: CheckCircle2 },
  REFUNDED: { label: "Pembayaran Gagal / Kedaluwarsa", color: "text-red-600", bg: "bg-red-50", border: "border-red-200", icon: RotateCcw },
  PARTIALLY_REFUNDED: { label: "Refund Sebagian", color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200", icon: RotateCcw },
};

function loadSnapScript(clientKey, isProduction) {
  return new Promise((resolve, reject) => {
    if (window.snap) return resolve();
    const script = document.createElement("script");
    script.src = isProduction
      ? "https://app.midtrans.com/snap/snap.js"
      : "https://app.sandbox.midtrans.com/snap/snap.js";
    script.setAttribute("data-client-key", clientKey);
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

export default function PaymentStatus({ bookingId, bookingStatus, role }) {
  const [payment, setPayment] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [paying, setPaying] = useState(false);
  const [createError, setCreateError] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);

  const refreshPayment = () =>
    api.get(`/api/payments/booking/${bookingId}?sync=1`)
      .then((r) => setPayment(r.data))
      .catch(() => setPayment(null));

  const refreshBooking = () =>
    api.get(`/api/bookings/${bookingId}`)
      .then((r) => setBooking(r.data))
      .catch(() => setBooking(null));

  useEffect(() => {
    Promise.all([refreshPayment(), refreshBooking()]).finally(() => setLoading(false));
  }, [bookingId]);

  const checkPromo = async () => {
    if (!promoCode.trim() || !booking) return;
    setPromoChecking(true);
    setPromoError("");
    setPromoInfo(null);
    try {
      const { data } = await api.post("/api/promos/validate", {
        code: promoCode,
        amount: booking.quoted_amount,
        worker_id: booking.worker?.id,
      });
      setPromoInfo(data);
    } catch (err) {
      setPromoError(formatApiError(err.response?.data?.detail));
    } finally {
      setPromoChecking(false);
    }
  };

  // Poll while PENDING (sync with Midtrans — webhook can't reach localhost)
  // Also poll REFUNDED briefly to recover if Midtrans actually settled
  useEffect(() => {
    if (!payment || !["PENDING", "REFUNDED"].includes(payment.status)) return;
    const t = setInterval(refreshPayment, 3000);
    return () => clearInterval(t);
  }, [payment?.status, bookingId]);

  const handleCreatePayment = async () => {
    setCreating(true);
    setCreateError("");
    try {
      const payload = {};
      if (promoInfo) payload.promo_code = promoInfo.code;
      const { data } = await api.post(`/api/payments/booking/${bookingId}`, payload);
      setPayment(data);
      setPromoInfo(null);
      setPromoCode("");
      refreshBooking();
    } catch (err) {
      setCreateError(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const handlePay = async () => {
    if (!payment) return;
    setPaying(true);
    try {
      const { data } = await api.post(`/api/payments/${payment.id}/snap`, {});
      const { data: keyInfo } = await api.get("/api/payments/midtrans-client-key");
      await loadSnapScript(data.client_key || keyInfo.client_key, keyInfo.is_production);
      window.snap.pay(data.token, {
        onSuccess: () => refreshPayment(),
        onPending: () => refreshPayment(),
        onError: () => refreshPayment(),
        onClose: () => refreshPayment(),
      });
      // Also re-check shortly after close in case settlement is async
      setTimeout(refreshPayment, 2000);
      setTimeout(refreshPayment, 5000);
    } catch (err) {
      console.error("Snap error", err);
    } finally {
      setPaying(false);
    }
  };

  if (loading) return null;

  if (!payment && ["PENDING_WORKER", "PENDING_CUSTOMER", "ACCEPTED", "ON_PROGRESS"].includes(bookingStatus)) {
    return (
      <div className="card p-4 border-brand-100" data-testid="payment-section">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-bold text-brand-800 text-sm">Pembayaran</p>
            <p className="text-xs text-brand-400">Belum ada pembayaran untuk booking ini</p>
          </div>
          <button
            onClick={handleCreatePayment}
            disabled={creating}
            className="btn-primary !py-2 !px-3 text-sm"
            data-testid="create-payment"
          >
            <DollarSign className="h-4 w-4" />
            {creating ? "Membuat..." : "Bayar Sekarang"}
          </button>
        </div>
        {role === "customer" && (
          <div className="mt-3 pt-3 border-t border-brand-50">
            <label className="field-label flex items-center gap-2">
              <Ticket className="h-4 w-4" /> Kode Promo (opsional, potong fee platform)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoInfo(null); setPromoError(""); }}
                placeholder="cth. HEMAT20"
                className="field-input flex-1 uppercase !py-2 text-sm"
                data-testid="input-promo"
              />
              <button
                type="button"
                onClick={checkPromo}
                className="btn-ghost !py-2 px-4 text-sm"
                disabled={promoChecking || !promoCode.trim() || !booking}
                data-testid="check-promo"
              >
                {promoChecking ? "Cek..." : "Pakai"}
              </button>
            </div>
            {promoError && <p className="text-xs text-red-600 mt-1">{promoError}</p>}
            {promoInfo && (
              <p className="text-xs text-success-600 mt-1 flex items-center gap-1" data-testid="promo-applied">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Fee dipotong {formatRupiah(promoInfo.discount)}! Total bayar {formatRupiah(promoInfo.final_amount)}.
                {promoInfo.capped ? " (dibatasi maks. penghematan fee)" : ""}
              </p>
            )}
          </div>
        )}
        {createError && <p className="text-xs text-red-600 mt-2">{createError}</p>}
      </div>
    );
  }

  if (!payment) return null;

  const orderDiscount = booking?.discount_amount || 0;
  const hasPromo = !!booking?.promo_code;
  const grossPrice = booking?.quoted_amount || payment.amount;

  const cfg = STATUS_CONFIG[payment.status] || STATUS_CONFIG.PENDING;
  const Icon = cfg.icon;
  const isRefundedRetry =
    payment.status === "REFUNDED" &&
    ["PENDING_WORKER", "PENDING_CUSTOMER", "ACCEPTED", "ON_PROGRESS", "COMPLETED"].includes(bookingStatus);

  return (
    <div className={`card p-4 ${cfg.border} border`} data-testid="payment-status">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-xl ${cfg.bg}`}>
          <Icon className={`h-5 w-5 ${cfg.color}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <p className="font-bold text-brand-800 text-sm">Status Pembayaran</p>
            <span className={`chip text-xs ${cfg.bg} ${cfg.color}`}>{payment.status}</span>
          </div>
          <p className={`text-sm font-medium ${cfg.color} mb-2`}>{cfg.label}</p>

          {role === "worker" ? (
            <div className="space-y-1.5 text-sm">
              {orderDiscount > 0 && (
                <div className="flex justify-between">
                  <span className="text-brand-500">Harga jasa</span>
                  <span className="font-bold text-brand-800">Rp{grossPrice.toLocaleString("id-ID")}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-brand-500">Total Order</span>
                <span className="font-bold text-brand-800">Rp{payment.amount.toLocaleString("id-ID")}</span>
              </div>
              <div className="flex justify-between border-t border-brand-100 pt-1.5">
                <span className="text-brand-500">Potongan Admin ({payment.commission_pct}%)</span>
                {hasPromo ? (
                  <span className="text-brand-500">
                    Rp{Number(payment.commission_amount).toLocaleString("id-ID")} <span className="text-success-600">(promo fee aktif)</span>
                  </span>
                ) : (
                  <span className="text-red-500">-Rp{Number(payment.commission_amount).toLocaleString("id-ID")}</span>
                )}
              </div>
              <div className="flex justify-between border-t border-brand-100 pt-1.5 font-bold">
                <span className="text-brand-700">Total Kamu Terima</span>
                <span className="text-success-600">Rp{Number(payment.worker_payout).toLocaleString("id-ID")}</span>
              </div>
              {payment.status === "PARTIALLY_RELEASED" && (
                <>
                  <div className="flex justify-between pt-1">
                    <span className="text-success-600">✓ Diterima sekarang (30%)</span>
                    <span className="font-bold text-success-600">Rp{Number(payment.initial_release_amount).toLocaleString("id-ID")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-600">🔒 Dilindungi 7 hari (70%)</span>
                    <span className="font-bold text-blue-600">Rp{Number(payment.protected_amount).toLocaleString("id-ID")}</span>
                  </div>
                  {payment.protected_until && (
                    <p className="text-xs text-brand-400">
                      Cair otomatis: {new Date(payment.protected_until).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-brand-500">Harga jasa</span>
                <span className="font-bold text-brand-800">Rp{(orderDiscount > 0 ? grossPrice : payment.amount).toLocaleString("id-ID")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-brand-500">Customer Fee ({payment.customer_fee_pct}%)</span>
                <span className="text-amber-600">+Rp{Number(payment.customer_fee_amount).toLocaleString("id-ID")}</span>
              </div>
              {hasPromo && !orderDiscount && (
                <p className="text-xs text-success-600">Promo {booking?.promo_code} aktif (fee dipotong).</p>
              )}
              {orderDiscount > 0 && (
                <div className="flex justify-between">
                  <span className="text-brand-500">Diskon promo ({booking?.promo_code})</span>
                  <span className="text-success-600">-Rp{orderDiscount.toLocaleString("id-ID")} (subsidi platform)</span>
                </div>
              )}
              <div className="flex justify-between border-t border-brand-100 pt-1.5 font-bold">
                <span className="text-brand-700">Total Kamu Bayar</span>
                <span className="text-brand-800">Rp{(payment.amount + Number(payment.customer_fee_amount)).toLocaleString("id-ID")}</span>
              </div>
              {payment.status === "PARTIALLY_RELEASED" && (
                <p className="text-xs text-blue-600 pt-1">
                  🔒 70% dana pekerja dilindungi selama 7 hari sebagai jaminan.
                </p>
              )}
            </div>
          )}

          {payment.status === "PENDING" && role === "customer" && (
            <button
              onClick={handlePay}
              disabled={paying}
              className="btn-primary w-full mt-3 !py-2.5 text-sm"
              data-testid="pay-now"
            >
              <DollarSign className="h-4 w-4" />
              {paying ? "Membuka..." : `Bayar Rp${(payment.amount + Number(payment.customer_fee_amount)).toLocaleString("id-ID")}`}
            </button>
          )}

          {isRefundedRetry && role === "customer" && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-brand-400">
                Pembayaran sebelumnya gagal/kedaluwarsa. Silakan coba lagi.
              </p>
              <button
                onClick={handleCreatePayment}
                disabled={creating}
                className="btn-primary w-full !py-2.5 text-sm"
                data-testid="retry-payment"
              >
                <RotateCcw className="h-4 w-4" />
                {creating ? "Menyiapkan..." : "Coba Bayar Lagi"}
              </button>
            </div>
          )}

          {payment.payment_ref && (
            <p className="text-xs text-brand-400 mt-1">Ref: {payment.payment_ref}</p>
          )}
        </div>
      </div>
    </div>
  );
}
